import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  X, Plus, Minus, 
  UtensilsCrossed, ShoppingBag, Bed, User, Phone
} from 'lucide-react';

const OrderCart = ({
  cart,
  orderType,
  selectedTable,
  selectedRoom,
  tableOrders,
  discount,
  discountType,
  customerName,
  customerPhone,
  persons = 1,
  staffName = '',
  onUpdateQuantity,
  onRemoveFromCart,
  onDiscountChange,
  onDiscountTypeChange,
  onCustomerNameChange,
  onCustomerPhoneChange,
  onPersonsChange,
  onStaffChange,
  onOrderTypeChange,
  onOpenTableSelect,
  onOpenRoomSelect,
  onPlaceOrder,
  onSaveAndPrint,
  onSendToKitchen,
  onKitchenAndPrint,
  staffList = [],
  TAX_RATE = 0.18
}) => {
  // Calculations
  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalQuantity = () => cart.reduce((sum, item) => sum + item.quantity, 0);
  
  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    return discountType === 'percent' ? subtotal * (discount / 100) : Math.min(discount, subtotal);
  };
  
  const calculateTax = () => (calculateSubtotal() - calculateDiscount()) * TAX_RATE;
  const calculateTotal = () => calculateSubtotal() - calculateDiscount() + calculateTax();

  const orderTypes = [
    { value: 'dine_in', label: 'TABLE', icon: UtensilsCrossed },
    { value: 'takeaway', label: 'TAKEAWAY', icon: ShoppingBag },
    { value: 'room_service', label: 'ROOM', icon: Bed },
  ];

  const handleOrderTypeClick = (type) => {
    onOrderTypeChange?.(type);
    
    // Open selection dialogs based on type
    if (type === 'dine_in') {
      onOpenTableSelect?.();
    } else if (type === 'room_service') {
      onOpenRoomSelect?.();
    }
  };

  return (
    <div className="w-96 bg-white border-l flex flex-col">
      {/* Order Type Tabs */}
      <div className="flex border-b">
        {orderTypes.map(type => {
          const Icon = type.icon;
          const isActive = orderType === type.value;
          return (
            <button
              key={type.value}
              className={`flex-1 py-3 px-2 text-sm font-semibold transition-colors flex items-center justify-center gap-1 ${
                isActive 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleOrderTypeClick(type.value)}
              data-testid={`order-type-${type.value}`}
            >
              <Icon className="h-4 w-4" />
              {type.label}
            </button>
          );
        })}
      </div>

      {/* Table/Room Info Row */}
      <div className="flex items-center gap-2 p-3 border-b bg-gray-50">
        {orderType === 'dine_in' && (
          <>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">Table</span>
              <Button
                variant="outline"
                size="sm"
                className="w-16 h-8 text-center text-sm border-gray-300"
                onClick={onOpenTableSelect}
                data-testid="select-table-btn"
              >
                {selectedTable?.table_number || 'Select'}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">Persons</span>
              <Input 
                type="number"
                min="1"
                value={persons}
                onChange={(e) => onPersonsChange?.(parseInt(e.target.value) || 1)}
                className="w-12 h-8 text-center text-sm border-gray-300"
              />
            </div>
          </>
        )}
        
        {orderType === 'room_service' && (
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600">Room</span>
            <Button
              variant="outline"
              size="sm"
              className="w-20 h-8 text-center text-sm border-gray-300"
              onClick={onOpenRoomSelect}
              data-testid="select-room-btn"
            >
              {selectedRoom?.room_number || 'Select'}
            </Button>
          </div>
        )}
        
        {orderType === 'takeaway' && (
          <span className="text-sm text-gray-500 italic">Takeaway Order</span>
        )}

        <Select value={staffName} onValueChange={onStaffChange}>
          <SelectTrigger className="w-24 h-8 text-sm ml-auto">
            <SelectValue placeholder="Staff" />
          </SelectTrigger>
          <SelectContent>
            {staffList.length > 0 ? (
              staffList.map(staff => (
                <SelectItem key={staff} value={staff}>{staff}</SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="Staff 1">Staff 1</SelectItem>
                <SelectItem value="Staff 2">Staff 2</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Customer Details - Always Visible */}
      <div className="p-3 border-b bg-white space-y-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Customer Name"
            value={customerName}
            onChange={(e) => onCustomerNameChange?.(e.target.value)}
            className="text-sm h-8 flex-1"
            data-testid="customer-name-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Mobile Number"
            value={customerPhone}
            onChange={(e) => onCustomerPhoneChange?.(e.target.value)}
            className="text-sm h-8 flex-1"
            data-testid="customer-phone-input"
          />
        </div>
      </div>

      {/* Items Header */}
      <div className="flex items-center px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-600 border-b">
        <span className="flex-1">ITEMS</span>
        <span className="w-20 text-center">QTY.</span>
        <span className="w-20 text-right">PRICE</span>
      </div>

      {/* Cart Items */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {cart.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <UtensilsCrossed className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No items added</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.item_id} className="flex items-center gap-2 py-2 border-b border-gray-100">
                {/* Remove Button */}
                <button 
                  className="h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 flex-shrink-0"
                  onClick={() => onRemoveFromCart(item.item_id)}
                  data-testid={`remove-item-${item.item_id}`}
                >
                  <X className="h-3 w-3" />
                </button>
                
                {/* Item Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.item_name}</p>
                  {item.notes && (
                    <p className="text-xs text-orange-500">{item.notes}</p>
                  )}
                </div>
                
                {/* Quantity Controls */}
                <div className="flex items-center gap-1 w-20 justify-center">
                  <button 
                    className="h-6 w-6 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                    onClick={() => onUpdateQuantity(item.item_id, -1)}
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                  <button 
                    className="h-6 w-6 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                    onClick={() => onUpdateQuantity(item.item_id, 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                
                {/* Price */}
                <span className="w-20 text-right text-sm font-semibold text-gray-800">
                  {(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Totals Section */}
      <div className="border-t bg-gray-50 p-3 space-y-2">
        {/* Sub Total */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Sub Total</span>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">{totalQuantity()}</span>
            <span className="font-semibold text-gray-800 w-20 text-right">{calculateSubtotal().toFixed(2)}</span>
          </div>
        </div>

        {/* Discount */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Discount</span>
          <div className="flex items-center gap-2">
            {/* Percentage/Fixed Toggle */}
            <div className="flex items-center gap-1 bg-white rounded border px-2 py-1">
              <label className="flex items-center gap-1 cursor-pointer">
                <input 
                  type="radio" 
                  name="discountType" 
                  checked={discountType === 'percent'} 
                  onChange={() => onDiscountTypeChange('percent')}
                  className="text-red-500"
                />
                <span className="text-xs">%</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input 
                  type="radio" 
                  name="discountType" 
                  checked={discountType === 'fixed'} 
                  onChange={() => onDiscountTypeChange('fixed')}
                  className="text-red-500"
                />
                <span className="text-xs">₹</span>
              </label>
            </div>
            <Input 
              type="number" 
              min="0" 
              className="w-16 h-7 text-sm text-right" 
              value={discount} 
              onChange={(e) => onDiscountChange(parseFloat(e.target.value) || 0)} 
            />
          </div>
        </div>

        {/* Tax */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Tax ({(TAX_RATE * 100).toFixed(0)}%)</span>
          <span className="font-semibold text-gray-800 w-20 text-right">{calculateTax().toFixed(2)}</span>
        </div>

        {/* Total */}
        <div className="flex justify-between items-center bg-red-500 text-white rounded px-3 py-2 -mx-3 mt-2">
          <span className="font-bold text-lg">Total</span>
          <span className="font-bold text-xl">₹{calculateTotal().toFixed(2)}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-4 gap-1 p-2 border-t bg-white">
        <Button 
          className="bg-green-500 hover:bg-green-600 text-white text-xs font-semibold h-10"
          onClick={() => onPlaceOrder?.(false)}
          disabled={cart.length === 0}
          data-testid="pos-save-btn"
        >
          SAVE
        </Button>
        <Button 
          className="bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold h-10"
          onClick={onSaveAndPrint}
          disabled={cart.length === 0}
          data-testid="pos-save-print-btn"
        >
          SAVE & PRINT
        </Button>
        <Button 
          className="bg-red-500 hover:bg-red-600 text-white text-xs font-semibold h-10"
          onClick={onSendToKitchen}
          disabled={cart.length === 0}
          data-testid="pos-kot-btn"
        >
          KOT
        </Button>
        <Button 
          className="bg-red-700 hover:bg-red-800 text-white text-xs font-semibold h-10"
          onClick={onKitchenAndPrint}
          disabled={cart.length === 0}
          data-testid="pos-kot-print-btn"
        >
          KOT & PRINT
        </Button>
      </div>
    </div>
  );
};

export default OrderCart;
